/**
 * Normalize “patient offer” and “current bid” amounts across patient + provider UIs.
 * Shapes vary: flat bid row, `{ bid: { … } }`, or doctor list items with nested `matchRequest`.
 */

function toPositiveNumber(value) {
  if (value === undefined || value === null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Patient’s canonical offer for this bid channel (original affordability or latest patient counter).
 * @param {object} bidLike
 * @param {number|string|undefined|null} [matchRequestAffordability] Root MR affordability when bid row omits it.
 * @returns {number|null}
 */
export function matchBidPatientOfferAmount(bidLike, matchRequestAffordability) {
  if (!bidLike || typeof bidLike !== "object") return null;

  const patientAction = String(
    bidLike.patientAction ?? bidLike.bid?.patientAction ?? bidLike.patientResponse ?? ""
  ).toLowerCase();

  const patientCounter = toPositiveNumber(
    bidLike.patientCounterAmount ?? bidLike.bid?.patientCounterAmount
  );
  if (patientAction === "countered" && patientCounter != null) return patientCounter;

  const fromBid = toPositiveNumber(
    bidLike.affordabilityPrice ??
      bidLike.yourBidPrice ??
      bidLike.bid?.affordabilityPrice ??
      bidLike.matchRequest?.affordabilityPrice
  );
  if (fromBid != null) return fromBid;

  return toPositiveNumber(matchRequestAffordability);
}

/**
 * Amount to show or sort as the “current” deal / bid for this provider row.
 * @param {object} bidLike
 * @param {number|string|undefined|null} [matchRequestAffordability]
 * @returns {number|null}
 */
export function matchBidTableAmount(bidLike, matchRequestAffordability) {
  if (!bidLike || typeof bidLike !== "object") return null;

  const providerAction = String(
    bidLike.providerAction ?? bidLike.bid?.providerAction ?? bidLike.status ?? ""
  ).toLowerCase();
  const patientAction = String(
    bidLike.patientAction ?? bidLike.bid?.patientAction ?? bidLike.patientResponse ?? ""
  ).toLowerCase();

  const patientOffer = matchBidPatientOfferAmount(bidLike, matchRequestAffordability);
  const rev = toPositiveNumber(
    bidLike.reverseBidAmount ?? bidLike.reverseBidPrice ?? bidLike.bid?.reverseBidAmount
  );

  if (providerAction === "accepted_patient_price") return patientOffer;

  if (providerAction === "countered") {
    if (patientAction === "accepted" && rev != null) return rev;
    if (patientAction === "pending" && rev != null) return rev;
    if (patientAction === "countered") {
      const pc = toPositiveNumber(bidLike.patientCounterAmount ?? bidLike.bid?.patientCounterAmount);
      if (pc != null) return pc;
    }
    if (rev != null) return rev;
    return patientOffer;
  }

  if (providerAction === "pending" || providerAction === "") return patientOffer;

  if (providerAction === "rejected") return patientOffer;

  return patientOffer;
}
